import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppSelector } from '@store/index';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { useSwrJobAudioInterview } from '@hooks/useSwrApi/endpoints/job';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import {PhoneIcon} from "@heroicons/react/24/solid";
import {PulsatingBestieAvatar} from "@components/templates/audio-interview/partials/PulsatingBestieAvatar";

interface AudioInterviewJobV2Props {
  slug: string;
  locale: Locale;
  scope?: 'job';
  onComplete?: () => void;
}

const AudioInterviewJobV2: React.FC<AudioInterviewJobV2Props> = ({
  slug,
  locale,
  scope = 'job',
  onComplete,
}) => {
  // Explicitly type t as accepting string keys
  const { t } = useTranslation('common') as { t: (key: string, options?: any) => string };
  const userResume = useAppSelector((state) => state.userOwnResume.userResume);
  // Audio permissions are not needed for this modal; call is made on device.
  const {
    trigger: triggerAudioInterview,
    isMutating: isStarting,
    error: startError,
  } = useSwrJobAudioInterview({ slug, locale }); // remove toastsOptions
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected'>('idle');
  const [localError, setLocalError] = useState<string | null>(null);
  const hasDisconnectedRef = useRef(false);

  const startInterview = async () => {
    hasDisconnectedRef.current = false;
    setLocalError(null);
    setConnectionState('connecting');
    try {
      const response = await triggerAudioInterview();
      if (response && typeof onComplete === 'function') {
        onComplete();
        setConnectionState('idle');
      }
    } catch (e) {
      // If error occurs, connectionState is handled by useEffect or here
      setConnectionState('idle');
    }
  };

  const handleStartInterview = async () => {
    if (scope === 'job' && !slug) return;
    // Track on tap: this is an outbound-call trigger with no in-app end signal to hook.
    sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'audio', subject: 'job' });
    await startInterview();
  };

  useEffect(() => {
    if (startError && connectionState === 'connecting') {
      // V1 API error structure: { errors: [{ message: string, ... }] }
      const message = startError?.errors?.[0]?.message || t('audio-interview.job-landing.error');
      setLocalError(message);
      setConnectionState('idle');
    }
  }, [startError, connectionState, t]);

  // Interview Room (connecting/connected)
  if ((connectionState === 'connecting' || connectionState === 'connected') && !hasDisconnectedRef.current) {
    // Show loading spinner while starting the call
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center">
        <TailwindSpinner className="size-8" />
        <p className="mt-4 text-center text-lg font-medium text-ink">{t('audio-interview.job-landing.cta-loading')}</p>
      </div>
    );
  }



  // Main Landing UI (Design inspired by provided HTML, using Bestie asset)
  return (
    <div className="mx-auto w-full max-w-3xl space-y-12 text-center">
      <h1 className="text-4xl font-extrabold tracking-tight md:text-5xl">
        {t('audio-interview.job-landing.title')}
      </h1>
      <div className="flex justify-center">
        <div className="relative">
          <div className="flex flex-1 flex-col items-center justify-center">
            <PulsatingBestieAvatar
              isConnected={false}
            />
          </div>
        </div>
      </div>
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 rounded-3xl border border-gray-100 bg-surface-50 p-6 text-left shadow-sm md:flex-row md:p-8">
        <div>
          <p className="text-lg font-medium leading-relaxed md:text-xl">
            {t('audio-interview.job-landing.info')}
          </p>
        </div>
      </div>
      <div className="pt-4">
        <button
          className="mx-auto flex items-center gap-3 rounded-full bg-secondary px-12 py-5 text-xl font-bold text-white shadow-lg transition-all hover:bg-secondary-dark hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleStartInterview}
          disabled={isStarting}
        >
          <PhoneIcon className="size-5" />
          {isStarting ? t('audio-interview.job-landing.cta-loading', { defaultValue: 'Starting...' }) : t('audio-interview.job-landing.cta')}
        </button>
        {localError && (
          <div className="pb-2 pt-4 text-sm font-medium text-red-600">
            {localError}
          </div>
        )}
      </div>
      {/* InterviewTypeSwitcher is rendered by parent, do not duplicate */}
    </div>
  );
};

export default AudioInterviewJobV2;
